import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import type { TeamRole } from '@/data/types/team.types'
import NotFoundPage from '@/pages/NotFoundPage'

// Route permission definitions
const ROUTE_PERMISSIONS: Record<string, TeamRole[]> = {
  '/organization/api-keys': ['OWNER', 'ADMIN'],
  '/organization/team': ['OWNER', 'ADMIN'],
  '/organization/settings': ['OWNER', 'ADMIN'],
  '/organization/billing': ['OWNER'],
}

const ORGANIZATION_SETUP_PATH = '/auth/organization-setup'
const DEFAULT_REDIRECT_PATH = '/overview'

// Check if a user role has permission to access a route
function hasRoutePermission(userRole: TeamRole, route: string): boolean {
  const allowedRoles = ROUTE_PERMISSIONS[route]
  if (!allowedRoles) {
    // Route not in permissions map - allow access (for non-organization routes)
    return true
  }
  return allowedRoles.includes(userRole)
}

// Shared loading component
function LoadingSpinner() {
  return (
    <div className='flex h-screen items-center justify-center'>
      <div className='text-center'>
        <div className='mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]' />
        <p className='text-muted-foreground'>Loading...</p>
      </div>
    </div>
  )
}

// Get redirect path from location state or use default
function getRedirectPath(location: ReturnType<typeof useLocation>): string {
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname
  return from || DEFAULT_REDIRECT_PATH
}

interface ProtectedRouteProps {
  children: React.ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, organization, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return <LoadingSpinner />
  }

  if (!user) {
    return <Navigate to='/auth/login' state={{ from: location }} replace />
  }

  const isAuthRoute = location.pathname.startsWith('/auth/')
  const isOrganizationSetup = location.pathname === ORGANIZATION_SETUP_PATH

  // User needs to set up organization
  if (!organization) {
    return isOrganizationSetup ? <>{children}</> : <Navigate to={ORGANIZATION_SETUP_PATH} replace />
  }

  // User has organization but is on auth route - redirect to app
  if (organization && isAuthRoute) {
    return <Navigate to={getRedirectPath(location)} replace />
  }

  return <>{children}</>
}

interface PublicOnlyRouteProps {
  children: React.ReactNode
}

export function PublicOnlyRoute({ children }: PublicOnlyRouteProps) {
  const { user, organization, loading } = useAuth()

  if (loading) {
    return <LoadingSpinner />
  }

  if (!user) {
    return <>{children}</>
  }

  // Authenticated user should be redirected to app
  if (!organization) {
    return <Navigate to={ORGANIZATION_SETUP_PATH} replace />
  }

  return <Navigate to={DEFAULT_REDIRECT_PATH} replace />
}

interface RoleProtectedRouteProps {
  children: React.ReactNode
}

export function RoleProtectedRoute({ children }: RoleProtectedRouteProps) {
  const { user, loading, organization } = useAuth()
  const location = useLocation()

  if (loading) {
    return <LoadingSpinner />
  }

  if (!user) {
    return <Navigate to='/auth/login' state={{ from: location }} replace />
  }

  // Check if user has organization and permission for this route
  if (!organization || !hasRoutePermission(organization.role, location.pathname)) {
    return <NotFoundPage />
  }

  return <>{children}</>
}

